import { useState, useMemo, useEffect } from 'react';
import type { ResponseNode } from '../../../types/wizardTypes';
import { parseResponseToTree } from '../converters/responseSchemaParser';
import { safeJsonParse } from '../../../utils/security';
import { useConnectionsStore } from '../../../store/connectionsStore';
import TemplateEditor from '../../../components/templateEditor/TemplateEditor';
import AiGenerator from '../../../components/aiGenerator/AiGenerator';
import { AiGenerationTypes } from '../../../enums/aiGenerationTypes';
import InfoLabel from '../../../ui/InfoLabel/InfoLabel';
import { DEFAULT_RESPONSE_TEMPLATE } from '../constants';
import { FileJson, Sparkles } from 'lucide-react';
import styles from './ResponseMappingStep.module.css';

interface ResponseMappingStepProps {
  watch: any;
  setValue: any;
  canEdit: boolean;
}

// function ResponseTreeNode({ node, depth }: { node: ResponseNode; depth: number }) {
//   const [expanded, setExpanded] = useState(true);
//   const hasChildren = node.children && node.children.length > 0;
//   const paddingLeft = depth * 20 + 8;

//   const typeClass = styles[`type${node.type.charAt(0).toUpperCase() + node.type.slice(1)}`] || styles.typeString;

//   return (
//     <>
//       <div className={styles.treeNode} style={{ paddingLeft }}>
//         {hasChildren ? (
//           <button
//             type="button"
//             className={styles.toggleBtn}
//             onClick={() => setExpanded(!expanded)}
//             aria-label={expanded ? 'Collapse' : 'Expand'}
//           >
//             {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
//           </button>
//         ) : (
//           <span className={styles.togglePlaceholder} />
//         )}
//         <span className={styles.nodeKey}>{node.key}</span>
//         <span className={`${styles.typeBadge} ${typeClass}`}>{node.type}</span>
//         {node.example !== undefined && (
//           <span className={styles.nodeExample}>{node.example}</span>
//         )}
//       </div>
//       {hasChildren && expanded && node.children!.map((child, i) => (
//         <ResponseTreeNode key={`${child.path}-${i}`} node={child} depth={depth + 1} />
//       ))}
//     </>
//   );
// }

export default function ResponseMappingStep({
  watch,
  setValue,
  canEdit,
}: ResponseMappingStepProps) {
  const [aiLoading, setAiLoading] = useState(false);
  const connectors = useConnectionsStore(s => s.connectors);
  const mainConnectionId = watch('mainConnectionId');

  useEffect(() => {
    const connector = connectors.find(c => c.id === mainConnectionId);
    const isMssql = connector?.connectionTypeId === 7;
    if (!isMssql && !watch('responseTemplate')) {
      setValue('responseTemplate', DEFAULT_RESPONSE_TEMPLATE);
    }
  }, [connectors, mainConnectionId, setValue, watch]);

  const sampleResponse = watch('sampleResponse') || '';

  const parsedTree = useMemo<ResponseNode[]>(() => {
    if (!sampleResponse.trim()) return [];
    const result = safeJsonParse(sampleResponse, null);
    if (!result.success || result.data === null) return [];
    return parseResponseToTree(result.data);
  }, [sampleResponse]);

  const responseTemplate = watch('responseTemplate') || '';
  const description = watch('description') || '';
  const inputSchema = watch('inputSchema') || '{}';

  return (
    <div className={styles.step}>
      {/* Sample Response Input */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeading}>
            <FileJson size={16} />
            <h3 className={styles.sectionTitle}>Sample Response</h3>
            <InfoLabel content="Paste a sample JSON response from the API. The structure will be parsed and displayed below." />
          </div>
        </div>

        <TemplateEditor
          value={sampleResponse}
          letDynamic={false}
          onChange={(value) => setValue('sampleResponse', value, { shouldDirty: true })}
          height="200px"
          title="Sample Response JSON"
          placeholder='Paste a sample API response here, e.g. {"d": {"results": [...]}}'
          readOnly={!canEdit}
        />

        {/* {parsedTree.length > 0 && (
          <div className={styles.tree}>
            {parsedTree.map((node, i) => (
              <ResponseTreeNode key={`${node.path}-${i}`} node={node} depth={0} />
            ))}
          </div>
        )} */}

        {sampleResponse.trim() && parsedTree.length === 0 && (
          <div className={styles.treeEmpty}>
            Invalid JSON - paste a valid response object to see its structure.
          </div>
        )}
      </div>

      {/* Response Template */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeading}>
            <Sparkles size={16} />
            <h3 className={styles.sectionTitle}>Response Template</h3>
            <InfoLabel content="Jinja2 template that transforms the raw API response into the final output. Use AI to generate it from the sample response above." />
          </div>
        </div>

        {canEdit && sampleResponse.trim() && parsedTree.length > 0 && (
          <div className={styles.aiSection}>
            <AiGenerator
              icon={<Sparkles size={16} />}
              buttonText="Generate response template from sample"
              prompt={`Generate a Jinja2 response mapping template from the sample response`}
              setContent={(value) => setValue('responseTemplate', value, { shouldDirty: true })}
              loading={aiLoading}
              setAiLoading={setAiLoading}
              previousData={responseTemplate}
              generateBody={{
                type: AiGenerationTypes.responseMapping,
                description: description,
                context: {
                  sampleResponse: sampleResponse,
                  inputSchema: inputSchema,
                },
              }}
            />
          </div>
        )}

        <div className={styles.advancedInner}>
          <TemplateEditor
            value={responseTemplate}
            onChange={(value) => setValue('responseTemplate', value, { shouldDirty: true })}
            height="300px"
            title="Response Mapping Template"
            templateType="response"
            placeholder="Enter Jinja2 template for response mapping..."
            readOnly={!canEdit}
          />
        </div>
      </div>
    </div>
  );
}
